import React, { useState, useMemo, useEffect } from 'react';
import { get, round } from 'lodash';
import { Button, Typography } from '@telus-uds/components-web';
import { Formik, Form, Field } from 'formik';
import Delete from '@telus-uds/palette-allium/build/web/icons/Delete';
import Edit from '@telus-uds/palette-allium/build/web/icons/Edit';
import PropTypes from 'prop-types';

import { convertDbToLocal, } from './data';
import { microsoftLicensingOffering } from '../../../utils/constant';
import Modal from '../../../design/shared/modal';

import {
  ModalFieldLabel,
  ModalFooter,
  Table,
  Tr,
  Td,
  Th,
  TdImp,
  TdLabel
} from './css';
import { formatCurrency } from '../../../utils/currencyFormatter';
import { getServiceWithProductQuestions } from '../../../utils/productCatalog';
import request from '../../../utils/request';

// Function to get distinct categories from the data
const getDistinctCategories = list => {
  return [...new Set(list.map(item => item.Category))];
};

// Function to get distinct item description line 2 for a given category
const getDistinctItemDescriptionLine2 = (list, category) => {
  const filteredData = list.filter(item => item.Category === category);
  return [
    ...new Set(filteredData.map(item => item['Item Description Line 2']))
  ];
};

const FmMicrosoftLicensing = ({ onChange, defaultValues, orderData }) => {
  const [isModalVisible, setModalVisible] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [data ,setLocalData] = useState([]);
  const [reRender,setReRender] = useState(false);
  const [
    microsoftlicensingSelections,
    setMicrosoftlicensingSelections
  ] = useState([]);
  const [errors, setErrors] = useState();
  const setLatestData = (transformedData) => {
    const defaultSelections = get(defaultValues, 'selections', []);
    const tempArray = [...defaultSelections]; // Create a copy of the selections array to avoid direct mutations.
  
    const updatedSelections = tempArray.filter((option) => {
      const matchingSelection = transformedData.find(
        (selection) => selection.optionId === option.optionId
      );
  
      if (matchingSelection) {
        // If a match is found, update the option with new data.
        const {
          Category = "Unknown",
          Cost = 0,
          Item = "N/A",
          "Item Description Line 2": ItemDescription = "None",
          Price = 0,
          "Subscription ID": SubscriptionID = "None",
          "Subscription Plan": SubscriptionPlan = "Free",
        } = matchingSelection;
  
        // Update the current option with transformed data.
        Object.assign(option, {
          cost: Cost,
          category: Category,
          item: Item,
          price: Price,
          subscriptionLineOption: ItemDescription,
          subscriptionPlan: SubscriptionPlan,
          totalCost: option.qtyOfLicenses * Cost,
          totalPrice: option.qtyOfLicenses * Price,
        });
  
        return true; // Keep this option in the array.
      } else {
        // If no match is found, remove the option.
        return false; // Filter this option out of the array.
      }
    });
  
    // Update the state with the updated selections array.
    setMicrosoftlicensingSelections(updatedSelections);
    setReRender(true);
  };
  
  const sortByIdxRecursively = (myData) => {
    if (Array.isArray(myData)) {
      // Sort the array by the 'ord' key if present
      myData.sort((a, b) => (a.ord !== undefined ? a.ord : Infinity) - (b.ord !== undefined ? b.ord : Infinity));
      // Recursively process each element of the array
      myData.forEach(sortByIdxRecursively);
    } else if (myData && typeof myData === 'object') {
      // If it's an object, process each key
      Object.values(myData).forEach(sortByIdxRecursively);
    }
  };

  const saveLicensingData = data => {
    const licensingData = data.find(
      item => item.name === microsoftLicensingOffering
    );
    if (!licensingData) {
      console.error(`Offering "${microsoftLicensingOffering}" not found in product catalog`);
      return;
    }
    const licensingService = getServiceWithProductQuestions(licensingData);
    if (!licensingService) {
      console.error(`No service found for offering "${microsoftLicensingOffering}"`);
      return;
    }
    const myData = licensingService.productCatalogQuestions || [];
    sortByIdxRecursively(myData);
    const transformedData = convertDbToLocal(myData);
    setLocalData(
        transformedData
    );
    setLatestData(transformedData);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await request.get('/productCatalog');
        if (response && response.data && response.data[0] && response.data[0].productCatalogOfferings && response.data[0].productCatalogOfferings[0]) {
         saveLicensingData( response.data[0].productCatalogOfferings);
          
        }
      } catch (error) {
        console.error("Error fetching product catalog:", error);
      } finally {
        setLoading(false); // Set loading to false once data fetching is complete
      }
    };
  
    fetchData();
  }, []);
  
  useEffect(() => {
    setMicrosoftlicensingSelections(get(defaultValues, 'selections', []));
  }, [orderData]);

  const categories = getDistinctCategories(data);
  const totals = useMemo(() => {
    let totalCostSum = 0;
    let totalPriceSum = 0;
    microsoftlicensingSelections.forEach(item => {
      totalCostSum += round(item.totalCost, 2);
      totalPriceSum += round(item.totalPrice, 2);
    });
    setReRender(false);
    return {
      totalCost: totalCostSum,
      totalPrice: totalPriceSum
    };
  }, [microsoftlicensingSelections,reRender]);

  const adjustedTotalPrice = useMemo(() => totals.totalPrice, [totals]);
  const adjustedTotalCost = useMemo(() => totals.totalCost, [totals]);

  const showModal = () => {
    setModalData(null);
    setModalVisible(true);
  };

  const onCloseModal = () => {
    setModalVisible(false);
  };

  const addLicencingRow = row => {
    if (row.qtyOfLicenses <= 0) {
      setErrors(prevErrors => ({
        ...prevErrors,
        qtyOfLicenses: 'Quantity of Licenses must be greater than 0'
      }));
      return;
    }
    setMicrosoftlicensingSelections([...microsoftlicensingSelections, row]);
  };

  const editItem = (e, row) => {
    e.preventDefault();
    setModalData(row);
    setModalVisible(true);
  };

  const handleWheel = e => e.target.blur();

  const deleteItem = (e, idx) => {
    e.preventDefault();
    setMicrosoftlicensingSelections([
      ...microsoftlicensingSelections.slice(0, idx),
      ...microsoftlicensingSelections.slice(idx + 1)
    ]);
  };

  useEffect(() => {
    onChange({
      selections: microsoftlicensingSelections,
      adjustedTotalPrice,
      adjustedTotalCost
    });
  }, [microsoftlicensingSelections, adjustedTotalPrice, adjustedTotalCost]);

  return (
    <div>
      {!!microsoftlicensingSelections.length && (
        <Table>
          <Th><Typography>Category</Typography></Th>
          <Th><Typography>Subscription Line Options</Typography></Th>
          <Th><Typography>Cost</Typography></Th>
          <Th><Typography>Price</Typography></Th>
          <Th><Typography>Qty of Licences</Typography></Th>
          <Th><Typography>Total Cost</Typography></Th>
          <Th><Typography>Total Price</Typography></Th>
          <Th colSpan={2}><Typography>Actions</Typography></Th>{' '}
          {/* Add this column for edit/delete buttons */}
          {microsoftlicensingSelections.map(
            (
              {
                category,
                subscriptionLineOption,
                cost,
                price,
                qtyOfLicenses,
                totalCost,
                totalPrice,categoryId,optionId
              },
              idx
            ) => (
              // eslint-disable-next-line react/no-array-index-key
              <Tr key={`${category}-${subscriptionLineOption}-${idx}`}>
                <Td><Typography>{category}</Typography></Td>
                <Td><Typography>{subscriptionLineOption}</Typography></Td>
                <Td><Typography>${cost}</Typography></Td>
                <Td><Typography>${price}</Typography></Td>
                <Td><Typography>{qtyOfLicenses}</Typography></Td>
                <Td><Typography>{formatCurrency(totalCost)}</Typography></Td>
                <Td><Typography>{formatCurrency(totalPrice)}</Typography></Td>
                <Td>
                  <Button
                    icon={Edit}
                    onPress={e =>
                      editItem(e, microsoftlicensingSelections[idx])
                    }
                    variant={{ text: true }}
                    accessibilityLabel="Edit"
                  />
                </Td>
                <Td>
                  <Button
                    icon={Delete}
                    onPress={e => deleteItem(e, idx)}
                    variant={{ text: true }}
                    accessibilityLabel="Delete"
                  />
                </Td>
              </Tr>
            )
          )}
          <Tr>
            <TdLabel colSpan={5}>Monthly Microsoft Licensing Total</TdLabel>
            <TdImp><Typography heading="h4" variant={{ size: 'h4' }}>{formatCurrency(totals.totalCost)}</Typography></TdImp>
            <TdImp><Typography heading="h4" variant={{ size: 'h4' }}>{formatCurrency(totals.totalPrice)}</Typography></TdImp>
            <Td />
          </Tr>
        </Table>
      )}
      <Button onClick={showModal}>Add Subscription</Button>
      <Modal
        open={isModalVisible}
        setOpen={onCloseModal}
        size="large"
        header={modalData ? 'Edit Subscription' : 'Add Subscription'}
      >
        <Formik
          initialValues={
            modalData || {
              category: '',
              categoryId:'',
              optionId:'',
              subscriptionLineOption: '',
              cost: 0,
              price: 0,
              qtyOfLicenses: 0,
              totalCost: 0,
              totalPrice: 0,
              subscriptionPlan: '',
              item: ''
            }
          }
          onSubmit={values => {
              if (modalData) {
              // Update existing row
              const updatedSelections = [...microsoftlicensingSelections];
              updatedSelections[
                microsoftlicensingSelections.indexOf(modalData)
              ] = values;
              setMicrosoftlicensingSelections(updatedSelections);
            } else {
              // Add new row
              addLicencingRow(values);
            }
            if (
              values.qtyOfLicenses !== 0 &&
              (!errors || !errors.qtyOfLicenses)
            ) {
              onCloseModal();
            } else {
              showModal();
            }
          }}
        >
          {({ values, setFieldValue, submitForm }) => (
            <Form>
              <div>
                <ModalFieldLabel>Category:</ModalFieldLabel>
                <Field
                  as="select"
                  id="category"
                  name="category"
                  className="frg-input-field color-light"
                  onChange={e => {
                    const selectedCategory = e.target.value;
                    setFieldValue('category', selectedCategory);
                    setFieldValue('subscriptionLineOption', ''); // Reset Subscription Line Option
                  }}
                  required
                >
                  <option value="">Select Category</option>
                  {categories.map(category => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </Field>
              </div>

              <div>
                <ModalFieldLabel htmlFor="subscriptionLineOption">
                  Subscription Line Option:
                </ModalFieldLabel>
                <Field
                  as="select"
                  id="subscriptionLineOption"
                  name="subscriptionLineOption"
                  className="frg-input-field color-light"
                  onChange={e => {
                    const selectedOption = e.target.value;
                    setFieldValue('subscriptionLineOption', selectedOption);
                    const selectedItem = data.find(
                      item =>
                        item.Category === values.category &&
                        item['Item Description Line 2'] === selectedOption
                    );
                    if (selectedItem) {
                      setFieldValue('cost', selectedItem.Cost);
                      setFieldValue('price', selectedItem.Price);
                      setFieldValue(
                        'totalCost',
                        round(selectedItem.Cost * values.qtyOfLicenses, 2)
                      );
                      setFieldValue(
                        'totalPrice',
                        round(selectedItem.Price * values.qtyOfLicenses, 2)
                      );
                      setFieldValue(
                        'subscriptionPlan',
                        selectedItem['Subscription Plan']
                      );
                      setFieldValue('item', selectedItem.Item);
                      setFieldValue('categoryId',selectedItem.categoryId);
                      setFieldValue('optionId',selectedItem.optionId);
                    }
                  }}
                  required
                >
                  <option value="">Select Subscription Line Option</option>
                  {values.category &&
                    getDistinctItemDescriptionLine2(data, values.category).map(
                      itemDesc => (
                        <option key={itemDesc} value={itemDesc}>
                          {itemDesc}
                        </option>
                      )
                    )}
                </Field>
              </div>

              <div>
                <ModalFieldLabel htmlFor="cost">Cost:</ModalFieldLabel>
                <Field
                  type="number"
                  id="cost"
                  name="cost"
                  className="frg-input-field color-light"
                  value={values.cost}
                  disabled
                  onWheel={handleWheel}
                  style={{ width: '100%' }}
                />
              </div>

              <div>
                <ModalFieldLabel htmlFor="price">Price:</ModalFieldLabel>
                <Field
                  type="number"
                  id="price"
                  name="price"
                  className="frg-input-field color-light"
                  value={values.price}
                  disabled
                  onWheel={handleWheel}
                  style={{ width: '100%' }}
                />
              </div>

              <div>
                <ModalFieldLabel htmlFor="qtyOfLicenses">
                  Qty of licenses:
                </ModalFieldLabel>
                <Field
                  type="number"
                  id="qtyOfLicenses"
                  name="qtyOfLicenses"
                  className="frg-input-field color-light"
                  onWheel={handleWheel}
                  onChange={e => {
                    const { value } = e.target;
                    setFieldValue('qtyOfLicenses', value);
                    setFieldValue('totalCost', round(values.cost * value, 2));
                    setFieldValue('totalPrice', round(values.price * value, 2));
                    setErrors(prevErrors => ({
                      ...prevErrors,
                      qtyOfLicenses:
                        value > 0
                          ? ''
                          : 'Quantity of Licenses must be greater than 0'
                    }));
                  }}
                  style={{ width: '100%' }}
                  required
                />
              </div>
              {errors && errors.qtyOfLicenses && (
                <div style={{ color: 'red' }}><Typography>{errors.qtyOfLicenses}</Typography></div>
              )}
              <div>
                <ModalFieldLabel htmlFor="totalCost">
                  Total Cost:
                </ModalFieldLabel>
                <Field
                  type="number"
                  id="totalCost"
                  name="totalCost"
                  className="frg-input-field color-light"
                  value={values.totalCost.toFixed(2)}
                  disabled
                  onWheel={handleWheel}
                  style={{ width: '100%' }}
                />
              </div>

              <div>
                <ModalFieldLabel htmlFor="totalPrice">
                  Total Price:
                </ModalFieldLabel>
                <Field
                  type="number"
                  id="totalPrice"
                  name="totalPrice"
                  className="frg-input-field color-light"
                  value={values.totalPrice.toFixed(2)}
                  disabled
                  onWheel={handleWheel}
                  style={{ width: '100%' }}
                />
              </div>
              <ModalFooter>
                <Button onClick={submitForm}>Add</Button>
              </ModalFooter>
            </Form>
          )}
        </Formik>
      </Modal>
    </div>
  );
};

FmMicrosoftLicensing.propTypes = {
  onChange: PropTypes.func.isRequired,
  defaultValues: PropTypes.shape({}).isRequired,
  orderData: PropTypes.shape({}).isRequired
};

export default FmMicrosoftLicensing;
